import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Alert, Button, Card, Input, Result, Segmented, Skeleton, Typography } from "antd";
import { ArrowLeftOutlined, FilePdfOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  changeOrderPhotoUrl,
  decideChangeOrder,
  errorText,
  getChangeOrder,
  openPortalFile
} from "./api";
import { SignatureForm } from "../../../serviceProvider/components/workReports/SignDialog";
import ChangeOrderPreview from "../../../serviceProvider/components/changeOrders/ChangeOrderPreview";
import { fullNameOf } from "./people";
import { isAuthenticated } from "../../../commons/Auth/auth";
import "../../../serviceProvider/components/workReports/workReports.css";
import "../../../serviceProvider/components/changeOrders/changeOrders.css";
import "./portal.css";

/**
 * A Nachtrag in the portal: the same view as the approval link, decided through the same
 * server logic (approve with signature, reject with a reason).
 */
const PortalChangeOrder = () => {
  const { t } = useTranslation("dashboard");
  const { id } = useParams();
  const [state, setState] = useState({ loading: true });
  const [choice, setChoice] = useState("approved");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const myName = fullNameOf(isAuthenticated());

  useEffect(() => {
    getChangeOrder(id)
      .then(res => setState({ loading: false, view: res.data.data }))
      .catch(err =>
        setState({ loading: false, failed: errorText(err, t("Change order not found")) })
      );
  }, [id, t]);

  const send = async body => {
    setBusy(true);
    setError("");
    try {
      const res = await decideChangeOrder(id, body);
      setState({ loading: false, view: res.data.data });
      setDone(body.status);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(errorText(err, t("Your answer could not be saved. Please try again.")));
    } finally {
      setBusy(false);
    }
  };

  const openPdf = async () => {
    try {
      await openPortalFile(`/change-orders/${id}/pdf`, `Nachtrag ${state.view.number}.pdf`);
    } catch (err) {
      setError(errorText(err, t("The PDF could not be created.")));
    }
  };

  if (state.loading) {
    return (
      <div className="portal-page">
        <Card>
          <Skeleton active paragraph={{ rows: 10 }} />
        </Card>
      </div>
    );
  }
  if (state.failed) {
    return (
      <div className="portal-page">
        <Result
          status="404"
          title={state.failed}
          extra={<Link to="/client/offers">{t("Back to the offers")}</Link>}
        />
      </div>
    );
  }

  const view = state.view;
  return (
    <div className="portal-page change-orders-page">
      <Link className="portal-back" to="/client/offers">
        <ArrowLeftOutlined aria-hidden="true" /> {t("Offers")}
      </Link>
      {done && (
        <Result
          status={done === "approved" ? "success" : "info"}
          title={
            done === "approved"
              ? t("Thank you, the change order is approved.")
              : t("Thank you, we have received your rejection.")
          }
        />
      )}
      <Card
        className="portal-card portal-gap"
        extra={
          <Button icon={<FilePdfOutlined />} onClick={openPdf}>
            PDF
          </Button>
        }
      >
        <ChangeOrderPreview view={view} photoSrc={photoId => changeOrderPhotoUrl(id, photoId)} />
      </Card>
      {view.canDecide && (
        <Card className="portal-card" title={t("Your decision")}>
          {error && <Alert className="portal-gap" type="error" showIcon message={error} />}
          <Segmented
            block
            value={choice}
            onChange={setChoice}
            options={[
              { value: "approved", label: t("Approve") },
              { value: "rejected", label: t("Reject") }
            ]}
            className="portal-gap"
          />
          {choice === "approved" ? (
            <>
              <Typography.Paragraph type="secondary">
                {t("With your signature you order this change at the price shown.")}
              </Typography.Paragraph>
              <div className="work-reports-page">
                <SignatureForm
                  defaultName={myName}
                  showCopy={false}
                  showLocation={false}
                  submitting={busy}
                  onSubmit={values =>
                    send({ status: "approved", name: values.name, image: values.image })
                  }
                />
              </div>
            </>
          ) : (
            <>
              <Input.TextArea
                value={note}
                maxLength={2000}
                autoSize={{ minRows: 3 }}
                placeholder={t("Why do you reject it?")}
                onChange={e => setNote(e.target.value)}
              />
              <Button
                className="portal-gap-top"
                danger
                size="large"
                block
                loading={busy}
                disabled={note.trim().length < 3}
                onClick={() => send({ status: "rejected", name: myName, note })}
              >
                {t("Reject change order")}
              </Button>
            </>
          )}
        </Card>
      )}
    </div>
  );
};

export default PortalChangeOrder;
